import { motion } from "framer-motion";
import { FileText, ClipboardList, Users, Camera, ArrowUpRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const RESOURCES = [
  {
    icon: ClipboardList,
    title: "The Assessment Day Checklist",
    desc: "Everything to prepare, bring and wear — so nothing on the day is a surprise.",
    content: [
      { h: "The week before", items: ["Research the airline's values and recent news — assessors notice", "Prepare 5–6 STAR stories (situation, task, action, result) from any job or life experience", "Sort your grooming: conservative hairstyle, natural makeup, nails short and neutral", "Print extra copies of your resume and any requested documents"] },
      { h: "The night before", items: ["Lay out your outfit: business attire, polished closed-toe shoes", "Check the venue, parking and arrival time — aim to be 15 minutes early", "Sleep. Tired candidates underperform in group exercises"] },
      { h: "On the day", items: ["Bring photo ID, your documents, a pen and a small notebook", "Phone on silent and away — from the car park onwards", "Smile and greet everyone: reception staff and other candidates are often feeding back to assessors", "During the reach test and height check, just relax — it's a safety requirement, not a judgement"] },
    ],
  },
  {
    icon: FileText,
    title: "10 Questions Airlines Always Ask",
    desc: "The behavioural questions that come up at nearly every cabin crew interview.",
    content: [
      { h: "The classics", items: ["Tell me about a time you dealt with a difficult customer", "Describe a time you worked in a team under pressure", "Tell me about a mistake you made and what you learned", "Give an example of when you went above and beyond for someone", "Tell me about a time you had to follow a rule you disagreed with"] },
      { h: "The cabin crew specials", items: ["Why do you want to be cabin crew — and why this airline?", "How would you handle a passenger who refuses to follow instructions?", "What would you do if a colleague wasn't pulling their weight?", "How do you feel about working irregular hours and being away from home?", "Describe a time you stayed calm in an emergency or high-stress situation"] },
      { h: "Tony's tip", items: ["Every answer should be a real story with a clear result. 'I'm a people person' tells the panel nothing — a story about calming an angry customer tells them everything."] },
    ],
  },
  {
    icon: Users,
    title: "Group Exercise Survival Guide",
    desc: "What assessors are really watching for when they put you in a group.",
    content: [
      { h: "What assessors score", items: ["Do you listen, or just wait for your turn to talk?", "Do you include quieter people in the discussion?", "Can you disagree politely and move the group forward?", "Do you keep an eye on the task and the time?"] },
      { h: "Common traps", items: ["Dominating the conversation — leadership is not volume", "Going silent because you're nervous — contribute early, even something small", "Criticising another candidate's idea instead of building on it", "Losing sight of the actual task while debating details"] },
      { h: "Tony's tip", items: ["The candidates who get hired are usually the ones who made the group better, not the ones who spoke the most. A simple 'Sarah, what do you think?' is worth more than a five-minute speech."] },
    ],
  },
  {
    icon: Camera,
    title: "Resume & Photo Standards",
    desc: "How to present yourself on paper the way airlines expect.",
    content: [
      { h: "Your resume", items: ["One page, clean layout, no photo unless the airline asks for one", "Lead with customer-facing experience: hospitality, retail, nursing, volunteering", "Quantify where you can: 'served 200+ customers daily' beats 'worked in a cafe'", "Tailor it to the airline — mention their name and values once, naturally"] },
      { h: "Your photos (if requested)", items: ["Full-length and headshot, taken in natural light against a plain background", "Business attire, groomed exactly as you would be for the assessment day", "Friendly, genuine smile — the panel is picturing you on board", "No filters, no heavy editing, no group shots"] },
      { h: "Tony's tip", items: ["Hiring teams spend under a minute on each application. Make yours easy to say yes to: tidy, relevant, and free of typos."] },
    ],
  },
];

export default function FreeResources() {
  return (
    <section id="resources" className="py-24 sm:py-32 bg-white" data-testid="resources-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-sky-700">Free resources</span>
          <h2 data-testid="resources-heading" className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 text-[#0A192F]">
            Start preparing right now
          </h2>
          <p className="text-slate-500 mt-3 text-sm leading-relaxed">
            Straight from Tony's hiring-team playbook. No sign-up, no email required — just click a guide.
          </p>
        </motion.div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {RESOURCES.map((r, i) => (
            <motion.div
              key={r.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
            >
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    data-testid={`resource-card-${i}`}
                    className="w-full text-left rounded-xl bg-slate-50 border border-slate-200 p-6 h-full flex flex-col hover:-translate-y-1 hover:shadow-lg hover:border-sky-300 transition-[transform,box-shadow,border-color] duration-300 group"
                  >
                    <div className="w-11 h-11 rounded-full bg-sky-100 flex items-center justify-center">
                      <r.icon className="w-5 h-5 text-sky-700" />
                    </div>
                    <h3 className="font-semibold text-[#0A192F] mt-4">{r.title}</h3>
                    <p className="text-sm text-slate-500 mt-2 leading-relaxed flex-1">{r.desc}</p>
                    <span className="mt-4 text-xs font-bold uppercase tracking-widest text-sky-700 flex items-center gap-1 group-hover:gap-2 transition-[gap] duration-200">
                      Read free guide <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </button>
                </DialogTrigger>
                <DialogContent data-testid={`resource-dialog-${i}`} className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-display uppercase text-2xl font-bold text-[#0A192F]">{r.title}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 mt-2">
                    {r.content.map((block) => (
                      <div key={block.h}>
                        <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-sky-700">{block.h}</h4>
                        <ul className="mt-3 space-y-2">
                          {block.items.map((item) => (
                            <li key={item} className="text-sm text-slate-600 leading-relaxed flex items-start gap-2.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-1.5 shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
